<template>
  <div>
    <div v-if="chartType === 'security'" class="scrollable">
      <h2 class="text-h5 text-center mb-6">
        Verified Security Event Trends
      </h2>
      <LineGraph
        title="Verified Events By Category"
        :chart-data="topicChartData"
        :preselected-legend="topicsSelectedLegend"
      >
      </LineGraph>
      <VerifiedEventsPieGraph
        title="Verified Events By Sector"
        :chart-data="charts.sectorChartData"
      >
      </VerifiedEventsPieGraph>
      <VerifiedEventsBarGraph
        title="Verified Events By Attack Type"
        :chart-data="charts.attackChartData"
        rotation="30"
      >
      </VerifiedEventsBarGraph>
      <VerifiedEventsBarGraph
        title="Verified Events By Perpetrator"
        :chart-data="charts.perpetratorChartData"
        rotation="30"
      >
      </VerifiedEventsBarGraph>
    </div>

    <div v-if="chartType === 'maritime'" class="scrollable">
      <h2 class="text-h5 text-center mb-6">
        Verified Maritime Event Trends
      </h2>
      <LineGraph
        title="Verified Events By Category"
        :chart-data="topicChartData"
        :preselected-legend="topicsSelectedLegend"
      >
      </LineGraph>
      <VerifiedEventsPieGraph
        title="Verified Events By Vessel Type"
        :chart-data="charts.vesselChartData"
      >
      </VerifiedEventsPieGraph>
      <VerifiedEventsBarGraph
        title="Verified Events By Time of Day"
        :chart-data="charts.timeChartData"
        rotation="45"
      >
      </VerifiedEventsBarGraph>
    </div>

    <div
      v-if="
        chartType === 'all' ||
        chartType === 'detected' ||
        chartType === 'hotspots'
      "
      class="scrollable"
    >
      <h2 class="text-h5 text-center mb-6">
        Event Trends
      </h2>

      <LineGraph
        v-if="chartType !== 'all'"
        title="Events By Category"
        :chart-data="topicChartData"
        :preselected-legend="topicsSelectedLegend"
      >
      </LineGraph>

      <LineGraph
        title="Events AI Emotions"
        :chart-data="charts.emotionChartData"
      >
      </LineGraph>

      <SentimentChart
        :histogram="sentimentHistogram"
        :if-loading="isLoading"
        title="Events AI Daily Sentiment"
        :include-refresh-button="false"
        :mark-line-interval="30"
      ></SentimentChart>
    </div>
  </div>
</template>

<script>
import { MODULE_PREFIX_SEARCH } from "@/store";
import { mapGetters } from "vuex";
import {
  formatChartData,
  formatTopicChartData,
} from "@/services/charts/charts.service";

export default {
  name: "EventsCharts",
  components: {
    LineGraph: () => import("@/components/events/LineGraph"),
    SentimentChart: () => import("@/components/common/details/SentimentChart"),
    VerifiedEventsPieGraph: () =>
      import("@/components/events/VerifiedEventsPieGraph"),
    VerifiedEventsBarGraph: () =>
      import("@/components/events/VerifiedEventsBarGraph"),
  },
  props: {
    verifiedEventsChartsData: {
      type: Object,
    },
  },
  data() {
    return {
      charts: {
        sectorChartData: [],
        perpetratorChartData: {},
        attackChartData: {},
        vesselChartData: [],
        timeChartData: {},
        emotionChartData: {},
        eventsSentimentData: [],
      },
    };
  },
  mounted() {
    this.getAllChartData();
  },
  computed: {
    ...mapGetters({
      details: MODULE_PREFIX_SEARCH + "charts",
    }),
    chartType() {
      return this.details.type;
    },
    chartsData() {
      return this.details.data;
    },
    sentimentHistogram() {
      return this.charts.eventsSentimentData
        ? this.charts.eventsSentimentData
        : [];
    },
    topicChartData() {
      let chartData = this.chartsData?.charts?.topicsByDate;

      return formatTopicChartData(chartData, this.chartType);
    },
    /**
     * Determines which series in the topic charts should be selected in the legend.
     * @returns {{}}
     */
    topicsSelectedLegend() {
      if (!this.topicChartData || !Object.keys(this.topicChartData).length) {
        return {};
      }

      //The Total series is always selected by default
      let selected = {
        Total: true,
      };

      //Only one other series should be selected in addition to Total
      let maxSelected = 2,
        numSelected = 1;

      this.topicChartData.series.forEach((series) => {
        if (typeof selected[series.name] === "undefined") {
          if (numSelected < maxSelected) {
            selected[series.name] = true;
            numSelected++;
          } else {
            selected[series.name] = false;
          }
        }
      });

      return selected;
    },
    isLoading() {
      return !this.chartsData;
    },
  },
  methods: {
    getAllChartData() {
      if (!this.isLoading) {
        let results = null;
        if (
          this.chartType === "all" ||
          this.chartType === "detected" ||
          this.chartType === "hotspots"
        ) {
          const options = {
            emotionChartData: true,
            eventsSentimentData: true,
            sectorChartData: false,
            attackChartData: false,
            perpetratorChartData: false,
            vesselChartData: false,
            timeChartData: false,
          };
          results = formatChartData(this.chartsData, options);
        } else if (this.chartType === "security") {
          const options = {
            emotionChartData: false,
            eventsSentimentData: false,
            sectorChartData: true,
            attackChartData: true,
            perpetratorChartData: true,
            vesselChartData: false,
            timeChartData: false,
          };
          results = formatChartData(this.chartsData, options);
          results.sectorChartData = results.sectorChartData.xAxis.map(function (
            value,
            index
          ) {
            return {
              name: value,
              value: results.sectorChartData.seriesData[index],
            };
          });
        } else if (this.chartType === "maritime") {
          const options = {
            emotionChartData: false,
            eventsSentimentData: false,
            sectorChartData: false,
            attackChartData: false,
            perpetratorChartData: false,
            vesselChartData: true,
            timeChartData: true,
          };
          results = formatChartData(this.chartsData, options);
          results.vesselChartData = results.vesselChartData.xAxis.map(function (
            value,
            index
          ) {
            return {
              name: value,
              value: results.vesselChartData.seriesData[index],
            };
          });
        }
        this.charts = results;
      }
    },
  },
  watch: {
    verifiedEventsChartsData() {
      this.chartsData = this.verifiedEventsChartsData;
    },
    details() {
      this.getAllChartData();
    },
  },
};
</script>

<style>
.scrollable {
  height: calc(100vh - 50px); /* 50px for header */
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 60px;
}
.chart-container {
  margin-bottom: 20px;
}
.theme--dark.v-sheet {
  background-color: #000000 !important;
  border-color: #000000 !important;
}
</style>
